/**
 * Type-safe обёртки гостевого раздела «Моё проживание» (UC-G, canon
 * design-guest v7 §7) + резолв деталей номера гостя (§3).
 *
 * Бэкенд (internal/stay, internal/room):
 *   GET  /v1/me/stays                  — проживания текущего гостя
 *                                        (ownership на бэке: ListStaysByGuest)
 *   POST /v1/stays/{id}/extensions     — запрос продления (UC-3.3-B, guest-flow)
 *   POST /v1/stays/{id}/late-checkout  — запрос позднего выезда (UC-3.3-C)
 *   GET  /v1/rooms/{id}                — номер гостя (type_id + number);
 *                                        GET-чтения открыты тенант-юзеру.
 *
 * Детали номера (тип/удобства/фото) берём из публичного каталога
 * GET /v1/public/{slug}/room-types и сопоставляем по type_id — публичный
 * эндпойнт несёт фото и удобства, в отличие от усечённого GET /v1/rooms/{id}.
 */
import { customFetch } from './openapi-fetch.js';
import type {
  RoomView,
  StayExtensionResponse,
  StayLateCheckoutResponse,
  StayResponse,
} from '@hsc/types';

/** GET /v1/me/stays — проживания текущего гостя (ownership на бэке). */
export async function getMyStays(params?: {
  limit?: number;
  offset?: number;
}): Promise<StayResponse[]> {
  const q: Record<string, string | number> = {};
  if (params?.limit !== undefined) q['limit'] = params.limit;
  if (params?.offset !== undefined) q['offset'] = params.offset;
  return customFetch<StayResponse[]>(
    Object.keys(q).length > 0
      ? { url: '/me/stays', method: 'GET', params: q }
      : { url: '/me/stays', method: 'GET' },
  );
}

/** GET /v1/rooms/{id} — номер гостя (резолв type_id/number для деталей). */
export async function getRoom(id: string): Promise<RoomView> {
  return customFetch<RoomView>({ url: `/rooms/${encodeURIComponent(id)}`, method: 'GET' });
}

/** Тело POST /v1/stays/{id}/extensions (запрос продления гостем). */
export interface RequestStayExtensionInput {
  /** Новая дата выезда (RFC3339). */
  new_to_date: string;
  /** Гость-инициатор (uid). */
  guest_id?: string;
  /** Комментарий гостя (необяз.). */
  reason?: string;
}

/** POST /v1/stays/{id}/extensions — запрос продления проживания. */
export async function requestStayExtension(
  stayId: string,
  input: RequestStayExtensionInput,
): Promise<StayExtensionResponse> {
  return customFetch<StayExtensionResponse>({
    url: `/stays/${encodeURIComponent(stayId)}/extensions`,
    method: 'POST',
    data: input,
  });
}

/** Тело POST /v1/stays/{id}/late-checkout (запрос позднего выезда). */
export interface RequestLateCheckoutInput {
  /** До какого времени гость просит остаться (RFC3339). */
  late_until: string;
  guest_id?: string;
  max_hours?: number;
}

/** POST /v1/stays/{id}/late-checkout — запрос позднего выезда. */
export async function requestStayLateCheckout(
  stayId: string,
  input: RequestLateCheckoutInput,
): Promise<StayLateCheckoutResponse> {
  return customFetch<StayLateCheckoutResponse>({
    url: `/stays/${encodeURIComponent(stayId)}/late-checkout`,
    method: 'POST',
    data: input,
  });
}

/**
 * POST /v1/me/stays/{id}/check-out — гость сам инициирует выселение
 * («Выселиться»). Бэк проверяет, что проживание принадлежит гостю (IDOR-safe),
 * переводит номер в «На проверке» и кидает заявку «Выселение» горничным.
 */
export async function checkOutMyStay(stayId: string): Promise<StayResponse> {
  return customFetch<StayResponse>({
    url: `/me/stays/${encodeURIComponent(stayId)}/check-out`,
    method: 'POST',
  });
}
